import React, { useMemo, useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'

const DEFAULT_WIDTH = 950
const DEFAULT_DEPTH = 800
const DEFAULT_LEG_HEIGHT = 150

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const computeGrid = (width, depth) => {
  const cols = width <= 900 ? 3 : width <= 1200 ? 4 : 5
  const rows = depth <= 900 ? 3 : depth <= 1200 ? 4 : 5
  return { rows, cols }
}

const linspace = (start, end, count) => {
  if (count <= 1) return [start]
  const step = (end - start) / (count - 1)
  return Array.from({ length: count }, (_, idx) => start + idx * step)
}

const buildLegs = ({ width, depth, rows, cols, setbacks, heights }) => {
  const usableWidth = Math.max(width - setbacks.left - setbacks.right, 0)
  const usableDepth = Math.max(depth - setbacks.front - setbacks.rear, 0)
  const xs = linspace(-width / 2 + setbacks.left, -width / 2 + setbacks.left + usableWidth, cols)
  const zs = linspace(-depth / 2 + setbacks.front, -depth / 2 + setbacks.front + usableDepth, rows)
  const legs = []

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const index = r * cols + c
      const isCorner = (r === 0 || r === rows - 1) && (c === 0 || c === cols - 1)
      legs.push({
        x: xs[c],
        z: zs[r],
        height: heights[index] || DEFAULT_LEG_HEIGHT,
        isCorner,
      })
    }
  }

  return legs
}

const pickThree = values => {
  if (values.length <= 3) return values
  const mid = Math.floor(values.length / 2)
  return [values[0], values[mid], values[values.length - 1]]
}

const buildFingerHoles = ({ width, depth, rows, cols, selectedSides }) => {
  const inset = 80
  const positions = []
  const halfW = width / 2
  const halfD = depth / 2
  const xs = linspace(-halfW, halfW, cols)
  const zs = linspace(-halfD, halfD, rows)
  const interiorXs = pickThree(xs.slice(1, -1))
  const interiorZs = pickThree(zs.slice(1, -1))

  selectedSides.forEach(side => {
    if (side === 'Front') {
      interiorXs.forEach(x => positions.push({ x, z: halfD - inset }))
    }
    if (side === 'Rear') {
      interiorXs.forEach(x => positions.push({ x, z: -halfD + inset }))
    }
    if (side === 'Left') {
      interiorZs.forEach(z => positions.push({ x: -halfW + inset, z }))
    }
    if (side === 'Right') {
      interiorZs.forEach(z => positions.push({ x: halfW - inset, z }))
    }
  })

  return positions
}

const buildGridLines = ({ width, depth, rows, cols }) => {
  const halfW = width / 2
  const halfD = depth / 2
  const xs = linspace(-halfW, halfW, cols + 1)
  const zs = linspace(-halfD, halfD, rows + 1)
  const points = []

  xs.forEach(x => {
    points.push(x, 0, -halfD, x, 0, halfD)
  })
  zs.forEach(z => {
    points.push(-halfW, 0, z, halfW, 0, z)
  })

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3))
  return geometry
}

const ShowerPreview = ({ width, depth, setbacks, heights, drain, fingerSides, focusedLegIndex }) => {
  const scale = 0.001
  const thickness = 40
  const baseHeight = thickness * scale
  const { rows, cols } = computeGrid(width, depth)

  const legs = useMemo(() => {
    return buildLegs({ width, depth, rows, cols, setbacks, heights })
  }, [width, depth, rows, cols, setbacks, heights])

  const drainPos = useMemo(() => {
    const x = width / 2 - drain.right
    const z = depth / 2 - drain.front
    return [x * scale - (width * scale) / 2, baseHeight / 2 + 0.003, z * scale - (depth * scale) / 2]
  }, [width, depth, drain, baseHeight, scale])

  const fingerHoles = useMemo(() => {
    return buildFingerHoles({ width, depth, rows, cols, selectedSides: fingerSides })
  }, [width, depth, rows, cols, fingerSides])

  const gridGeometry = useMemo(() => buildGridLines({ width, depth, rows, cols }), [width, depth, rows, cols])

  return (
    <div className="preview-card">
      <div className="preview-header">3D Preview</div>
      <div className="preview-stage">
        <Canvas camera={{ position: [0.8, 0.6, 0.9], fov: 45 }}>
          <ambientLight intensity={0.65} />
          <directionalLight position={[1, 1.5, 1]} intensity={0.8} />
          <directionalLight position={[-1, 1, -1]} intensity={0.35} />
          <mesh>
            <boxGeometry args={[width * scale, baseHeight, depth * scale]} />
            <meshStandardMaterial color="#8d97ab" transparent opacity={0.6} />
          </mesh>
          <lineSegments geometry={gridGeometry} position={[0, baseHeight / 2 + 0.0005, 0]} scale={[scale, 1, scale]}>
            <lineBasicMaterial color="#6f7a90" linewidth={1} />
          </lineSegments>
          {legs.map((leg, index) => {
            const legHeight = Math.max(leg.height, 40) * scale
            const isFocused = focusedLegIndex === index
            return (
              <mesh
                key={`leg-${index}`}
                position={[leg.x * scale, -baseHeight / 2 - legHeight / 2, leg.z * scale]}
              >
                <cylinderGeometry args={[0.02, 0.02, legHeight, 16]} />
                <meshStandardMaterial color={isFocused ? '#f07a28' : '#7f8aa1'} />
              </mesh>
            )
          })}
          <mesh position={drainPos}>
            <cylinderGeometry args={[Math.max(drain.diameter, 20) * 0.5 * scale, Math.max(drain.diameter, 20) * 0.5 * scale, 0.01, 24]} />
            <meshStandardMaterial color="#2a6df5" />
          </mesh>
          {fingerHoles.map((hole, index) => (
            <mesh key={`hole-${index}`} position={[hole.x * scale, baseHeight / 2 + 0.008, hole.z * scale]}>
              <sphereGeometry args={[0.01, 16, 16]} />
              <meshStandardMaterial color="#2bb673" />
            </mesh>
          ))}
          <OrbitControls enablePan={false} />
        </Canvas>
      </div>
      <div className="preview-footer">Drag to rotate · {rows * cols} leg supports</div>
    </div>
  )
}

const StepHeader = ({ title, subtitle }) => (
  <div className="step-header">
    <h2>{title}</h2>
    <p>{subtitle}</p>
  </div>
)

const StepControls = ({ step, setStep, totalSteps }) => (
  <div className="step-controls">
    <button
      type="button"
      className="ghost"
      onClick={() => setStep(Math.max(step - 1, 1))}
      disabled={step === 1}
    >
      Previous
    </button>
    <div className="step-indicator">Step {step} of {totalSteps}</div>
    <button
      type="button"
      className="primary"
      onClick={() => setStep(Math.min(step + 1, totalSteps))}
      disabled={step === totalSteps}
    >
      Continue
    </button>
  </div>
)

const InputRow = ({ label, children, hint }) => (
  <div className="input-row">
    <label>
      <span>{label}</span>
      {children}
    </label>
    {hint && <small>{hint}</small>}
  </div>
)

const ShowerConfigurator = () => {
  const [step, setStep] = useState(1)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [depth, setDepth] = useState(DEFAULT_DEPTH)
  const [entrySide, setEntrySide] = useState('Right')
  const [setbacks, setSetbacks] = useState({ front: 50, rear: 50, left: 50, right: 50 })
  const [heights, setHeights] = useState([])
  const [quickHeight, setQuickHeight] = useState(DEFAULT_LEG_HEIGHT)
  const [drain, setDrain] = useState({ diameter: 50, front: 200, right: 200 })
  const [fingerSides, setFingerSides] = useState(['Front'])
  const [focusedLegIndex, setFocusedLegIndex] = useState(null)

  const { rows, cols } = useMemo(() => computeGrid(width, depth), [width, depth])

  useEffect(() => {
    const size = rows * cols
    setHeights(prev => {
      if (prev.length === size) return prev
      return Array.from({ length: size }, (_, idx) => prev[idx] || DEFAULT_LEG_HEIGHT)
    })
  }, [rows, cols])

  const handleHeightChange = (index, value) => {
    setHeights(prev => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  const applyQuickHeight = () => {
    setHeights(prev => prev.map(() => quickHeight))
  }

  return (
    <div className="configurator">
      <div className="form-panel">
        {step === 1 && (
          <>
            <StepHeader
              title="Base Dimensions"
              subtitle="Enter width and depth. Leg grid auto-updates based on size."
            />
            <InputRow label="Width (mm)" hint="Valid range: 600 - 1500mm">
              <input
                type="number"
                inputMode="decimal"
                value={width}
                min={600}
                max={1500}
                onChange={e => setWidth(clamp(Number(e.target.value || 0), 600, 1500))}
              />
            </InputRow>
            <InputRow label="Depth (mm)" hint="Valid range: 600 - 1200mm">
              <input
                type="number"
                inputMode="decimal"
                value={depth}
                min={600}
                max={1200}
                onChange={e => setDepth(clamp(Number(e.target.value || 0), 600, 1200))}
              />
            </InputRow>
            <div className="info-card">
              <div>Grid Layout: {cols} × {rows}</div>
              <div>Total Leg Supports: {rows * cols} qty</div>
            </div>
            <div className="entry-grid">
              {['Front', 'Rear', 'Left', 'Right'].map(side => (
                <button
                  key={side}
                  type="button"
                  className={entrySide === side ? 'chip active' : 'chip'}
                  onClick={() => setEntrySide(side)}
                >
                  {side}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <StepHeader
              title="Leg Grid & Heights"
              subtitle="Set heights per leg or apply a single height to all."
            />
            <div className="quick-apply">
              <input
                type="number"
                inputMode="decimal"
                value={quickHeight}
                min={40}
                max={400}
                onChange={e => setQuickHeight(Number(e.target.value || 0))}
              />
              <button type="button" className="primary" onClick={applyQuickHeight}>
                Apply to All
              </button>
            </div>
            <div className="leg-grid">
              {heights.map((height, index) => {
                const row = Math.floor(index / cols)
                const col = index % cols
                const isCorner = (row === 0 || row === rows - 1) && (col === 0 || col === cols - 1)
                return (
                  <div key={`leg-${index}`} className={isCorner ? 'leg-card corner' : 'leg-card'}>
                    <div className="leg-label">
                      {String.fromCharCode(65 + row)}{col + 1}
                      {isCorner && <span>Corner</span>}
                    </div>
                    <input
                      type="number"
                inputMode="decimal"
                      value={height}
                      min={40}
                      max={400}
                      onChange={e => handleHeightChange(index, Number(e.target.value || 0))}
                      onFocus={() => setFocusedLegIndex(index)}
                      onBlur={() => setFocusedLegIndex(null)}
                    />
                    <small>mm</small>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <StepHeader
              title="Leg Setback Distance"
              subtitle="Distance from the edge to the center of the perimeter legs."
            />
            <div className="two-col">
              <InputRow label="Front Edge Setback (mm)">
                <input
                  type="number"
                inputMode="decimal"
                  value={setbacks.front}
                  min={0}
                  max={200}
                  onChange={e => setSetbacks(prev => ({ ...prev, front: Number(e.target.value || 0) }))}
                />
              </InputRow>
              <InputRow label="Rear Edge Setback (mm)">
                <input
                  type="number"
                inputMode="decimal"
                  value={setbacks.rear}
                  min={0}
                  max={200}
                  onChange={e => setSetbacks(prev => ({ ...prev, rear: Number(e.target.value || 0) }))}
                />
              </InputRow>
              <InputRow label="Left Edge Setback (mm)">
                <input
                  type="number"
                inputMode="decimal"
                  value={setbacks.left}
                  min={0}
                  max={200}
                  onChange={e => setSetbacks(prev => ({ ...prev, left: Number(e.target.value || 0) }))}
                />
              </InputRow>
              <InputRow label="Right Edge Setback (mm)">
                <input
                  type="number"
                inputMode="decimal"
                  value={setbacks.right}
                  min={0}
                  max={200}
                  onChange={e => setSetbacks(prev => ({ ...prev, right: Number(e.target.value || 0) }))}
                />
              </InputRow>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <StepHeader
              title="Drainage Information"
              subtitle="Specify drain diameter and position on the base."
            />
            <InputRow label="Drain Diameter (mm)" hint="Standard sizes: 50, 80, 90">
              <input
                type="number"
                inputMode="decimal"
                value={drain.diameter}
                min={20}
                max={120}
                onChange={e => setDrain(prev => ({ ...prev, diameter: Number(e.target.value || 0) }))}
              />
            </InputRow>
            <InputRow label="Drain Distance from Front to Centre (mm)">
              <input
                type="number"
                inputMode="decimal"
                value={drain.front}
                min={0}
                max={depth}
                onChange={e => setDrain(prev => ({ ...prev, front: Number(e.target.value || 0) }))}
              />
            </InputRow>
            <InputRow label="Drain Distance from Right to Centre (mm)">
              <input
                type="number"
                inputMode="decimal"
                value={drain.right}
                min={0}
                max={width}
                onChange={e => setDrain(prev => ({ ...prev, right: Number(e.target.value || 0) }))}
              />
            </InputRow>
          </>
        )}

        {step === 5 && (
          <>
            <StepHeader
              title="Finger Holes"
              subtitle="Select which sides should have finger holes for easier lifting."
            />
            <div className="entry-grid">
              {['Front', 'Rear', 'Left', 'Right'].map(side => {
                const active = fingerSides.includes(side)
                return (
                  <button
                    key={side}
                    type="button"
                    className={active ? 'chip active' : 'chip'}
                    onClick={() => {
                      setFingerSides(prev => (
                        prev.includes(side) ? prev.filter(item => item !== side) : [...prev, side]
                      ))
                    }}
                  >
                    {side} Side
                  </button>
                )
              })}
            </div>
            <div className="info-card">
              Selected: {fingerSides.length ? fingerSides.join(', ') : 'None'}
            </div>
          </>
        )}

        <StepControls step={step} setStep={setStep} totalSteps={5} />
      </div>

      <div className="preview-panel">
        <ShowerPreview
          width={width}
          depth={depth}
          setbacks={setbacks}
          heights={heights}
          drain={drain}
          fingerSides={fingerSides}
          focusedLegIndex={focusedLegIndex}
        />
      </div>
    </div>
  )
}

export default ShowerConfigurator
